import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { API_BASE_URL } from '@/common/config/api'

/**
 * Auth provider types supported by the system.
 * @typedef {'B2C' | 'Kinde'} AuthProviderType
 */

/**
 * Response from the auth provider endpoint.
 * @typedef {Object} AuthProviderResponse
 * @property {AuthProviderType} authProvider - The authentication provider for the user
 */

/**
 * RTK Query API for auth provider detection.
 *
 * This API is called BEFORE login to determine whether a user should
 * authenticate via Azure AD B2C or Kinde. It is part of the wave-based
 * migration strategy where users are gradually migrated to Kinde.
 *
 * IMPORTANT: This is an UNAUTHENTICATED API - no token injection.
 * It must work before the user has logged in.
 *
 * @see docs/KINDE_MIGRATION_PLAN.md for migration strategy details
 */

const apiVersion = 'v2'

export const authProviderApi = createApi({
    reducerPath: 'authProviderApi',
    baseQuery: fetchBaseQuery({
        baseUrl: API_BASE_URL,
        timeout: 10000, // Shorter timeout for login flow UX
    }),
    endpoints: (builder) => ({
        /**
         * Get the authentication provider for a given email address.
         *
         * Used during the login flow to route users to the correct auth provider:
         * - 'B2C' = Azure AD B2C (legacy)
         * - 'Kinde' = Kinde auth (new)
         *
         * @param {string} email - User's email address
         * @returns {AuthProviderResponse} The auth provider for this user
         *
         * @example
         * const [getAuthProvider] = useLazyGetAuthProviderQuery()
         * const { data } = await getAuthProvider('user@example.com')
         * // data.authProvider === 'B2C' or 'Kinde'
         */
        getAuthProvider: builder.query({
            query: (email) => `/${apiVersion}/auth/provider?email=${encodeURIComponent(email)}`,
            /**
             * Transform backend response to internal format.
             *
             * Backend returns: { provider: 'AzureAdB2C' | 'Kinde' }
             * Frontend expects: { authProvider: 'B2C' | 'Kinde' }
             */
            transformResponse: (response) => {
                const providerMap = {
                    AzureAdB2C: 'B2C',
                    Kinde: 'Kinde',
                }
                return {
                    authProvider: providerMap[response.provider],
                }
            },
        }),
    }),
})

export const { useGetAuthProviderQuery, useLazyGetAuthProviderQuery } = authProviderApi
